import API from "../../api";
import types from "../types";
import { HTTP } from "../../api/http-common";
import { DIETARY_ALLERGIES } from "../../constants";
import commonAPI from "../../api/common";

// initial state
const state = {
  accessToken: null,
  expiryTime: 0, // Duration before access token expires in milliseconds
  lastSaveTime: 0,
  isSaving: false,
  dietaryAllergies: DIETARY_ALLERGIES,
};

// getters
const getters = {
  [types.getters.accessToken]: (state) => state.accessToken,
  [types.getters.expiryTime]: (state) => state.expiryTime,
  [types.getters.lastSaveTime]: (state) => state.lastSaveTime,
  [types.getters.isSaving]: (state) => state.isSaving,
  [types.getters.dietaryAllergies]: (state) => state.dietaryAllergies,
};

// actions
const actions = {
  [types.actions.setAccessToken]({ commit }, { accessToken }) {
    commit(types.mutations.setAccessToken, accessToken);
  },
  [types.actions.setLastSaveTime]({ commit }, { lastSaveTime }) {
    commit(types.mutations.setLastSaveTime, lastSaveTime);
  },
  [types.actions.setIsSaving]({ commit }, { isSaving }) {
    commit(types.mutations.setIsSaving, isSaving);
  },
  [types.actions.refreshAccessToken]({ commit }) {
    return API.refreshAccessToken().then((res) => {
      if (res.accessToken) {
        commit(types.mutations.setAccessToken, res.accessToken);
        commit(types.mutations.setExpiryTime, res.expiryTime);
      }
    });
  },
  [types.actions.logout]({ commit }) {
    return API.logout().then((res) => {
      commit(types.mutations.removeAccessToken);
      return res;
    });
  },
  [types.actions.getDietaryAllergies]({ commit }) {
    return commonAPI.fetchDietaryAllergies().then((res) => {
      commit(types.mutations.setDietaryAllergies, res);
      return res;
    });
  },

  /**
   * Updates contacts, visa requirements, passport images
   * @param commit
   * @param contacts
   * @param visaRequirements
   * @param passports
   * @param showLoader
   * @returns {*}
   */
  [types.actions.updateContactsAndVisaRequirementsRemote](
    { commit },
    {
      contacts,
      visaRequirements,
      passports,
      showLoader = true,
      workflowTrigger = true,
    },
  ) {
    const changedContacts = contacts.filter((contact) => contact.isDirty);
    const changedVisaRequirements = visaRequirements.filter(
      (visaRequirement) => visaRequirement.isDirty,
    );
    const changedPassports = passports.filter((passport) => passport.isDirty);
    if (
      !changedContacts.length &&
      !changedVisaRequirements.length &&
      !changedPassports.length
    ) {
      return Promise.resolve(false);
    }

    showLoader && commit(types.mutations.setIsLoading, { isLoading: true });
    commit(types.mutations.setIsSaving, true);

    return API.updateContactsAndVisaRequirements(
      changedContacts,
      changedVisaRequirements,
      changedPassports,
      workflowTrigger, // tell backend whether Zoho automations should run
    )
      .then((res) => {
        if (workflowTrigger && changedContacts.length) {
          // only clear dirty flags on the final, workflow-enabled save
          commit(types.mutations.resetContactsDirtyFlag);
        }
        if (workflowTrigger && changedVisaRequirements.length) {
          res.updatedVisaRequirements.forEach((updatedVisaRequirement) => {
            commit(types.mutations.updateVisaRequirementEntity, {
              visaRequirement: updatedVisaRequirement,
            });
          });

          commit(types.mutations.resetVisaRequirementsDirtyFlag);
        }
        if (workflowTrigger && changedPassports.length) {
          res.updatedPassports.forEach((updatedPassport) => {
            commit(types.mutations.updatePassportEntity, {
              passport: updatedPassport,
            });
          });
          commit(types.mutations.resetPassportsDirtyFlag);
        }
        return res;
      })
      .finally(() => {
        showLoader &&
          commit(types.mutations.setIsLoading, { isLoading: false });
        commit(types.mutations.setIsSaving, false);
      });
  },
  [types.actions.updateContactsAndBookingRemote](
    { commit },
    {
      contacts,
      booking,
      contactBookings = [],
      showLoader = true,
      workflowTrigger = true,
    },
  ) {
    console.log(
      "[updateContactsAndBookingRemote] booking.isDirty:",
      booking && booking.isDirty,
    );

    const changedContacts = contacts.filter((contact) => contact.isDirty);
    const changedContactBookings = contactBookings.filter(
      (contactBooking) => contactBooking.isDirty,
    );
    if (
      !changedContacts.length &&
      !changedContactBookings.length &&
      ((booking && !booking.isDirty) || !booking)
    ) {
      return Promise.resolve(false);
    }
    const bookingPayload = booking && booking.isDirty ? booking : undefined;

    showLoader && commit(types.mutations.setIsLoading, { isLoading: true });

    return API.updateContactsAndBooking(
      changedContacts,
      bookingPayload,
      changedContactBookings,
      workflowTrigger,
    )
      .then((res) => {
        if (workflowTrigger && changedContacts.length) {
          commit(types.mutations.resetContactsDirtyFlag);
        }
        if (workflowTrigger && changedContactBookings.length) {
          commit(types.mutations.resetContactBookingsDirtyFlag);
        }
        if (workflowTrigger && bookingPayload) {
          // only clear booking dirty state after the final workflow-enabled sync
          commit(types.mutations.resetBookingDirtyFlag);
        }
        return res;
      })
      .finally(() => {
        showLoader &&
          commit(types.mutations.setIsLoading, { isLoading: false });
      });
  },

  [types.actions.updatePrePostArrangementsRemote](
    { commit },
    { prePostArrangements, showLoader = true },
  ) {
    showLoader && commit(types.mutations.setIsLoading, { isLoading: true });

    return API.updatePrePostArrangements(prePostArrangements)
      .then((res) => {
        // changedContacts.length && commit(types.mutations.resetContactsDirtyFlag)
        // if (changedVisaRequirements.length) {
        //   res.updatedVisaRequirements.forEach(updatedVisaRequirement => {
        //     commit(types.mutations.updateVisaRequirementEntity, { visaRequirement: updatedVisaRequirement })
        //   })
        //
        //   commit(types.mutations.resetVisaRequirementsDirtyFlag)
        // }
        return res;
      })
      .finally(() => {
        showLoader &&
          commit(types.mutations.setIsLoading, { isLoading: false });
      });
  },

  [types.actions.copyPrePostArrangement](
    { commit, getters },
    { preArrangements, postArrangements },
  ) {
    const primaryContact = getters[types.getters.primaryContact];

    preArrangements.forEach((preArrangement) => {
      const preArrangementToCopyFrom = getters[
        types.getters.preArrangementByContactIDAndCategory
      ](primaryContact.contactID, preArrangement.category);
      commit(types.mutations.copyPreArrangement, {
        preArrangement,
        preArrangementToCopyFrom,
      });
    });

    postArrangements.forEach((postArrangement) => {
      const postArrangementToCopyFrom = getters[
        types.getters.postArrangementByContactIDAndCategory
      ](primaryContact.contactID, postArrangement.category);
      commit(types.mutations.copyPostArrangement, {
        postArrangement,
        postArrangementToCopyFrom,
      });
    });
  },
};

// mutations
const mutations = {
  [types.mutations.setExpiryTime](state, expiryTime) {
    state.expiryTime = expiryTime;
  },
  [types.mutations.setAccessToken](state, accessToken) {
    HTTP.defaults.headers.common["Authorization"] = accessToken;
    state.accessToken = accessToken;
  },
  [types.mutations.setLastSaveTime](state, lastSaveTime) {
    state.lastSaveTime = lastSaveTime;
  },
  [types.mutations.setIsSaving](state, isSaving) {
    state.isSaving = isSaving;
  },
  [types.mutations.removeAccessToken](state) {
    // Remove Authorization header on successful logout
    delete HTTP.defaults.headers.common["Authorization"];

    state.accessToken = null;
  },
  [types.mutations.setDietaryAllergies](state, dietaryAllergies) {
    state.dietaryAllergies = dietaryAllergies;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/common.js